import { type AreaStates, formatPeriodLabel, formatPeriodShort, type Lang, type Level } from '@dc1/shared';

import type { TFunction } from 'i18next';
import { ApiError } from '../api/client';

type PeriodLike = { startsAt: string; endsAt: string; label: string | null };

/** Period label: the Leader's name for it if set, otherwise its date range in the UI language. */
export function periodLabel(period: PeriodLike, lang: Lang): string {
  return period.label ?? formatPeriodLabel(period, lang);
}

/** Compact label for chart axes and tabs: the Leader's name, or "01/11–30/11/26". */
export function periodShortLabel(period: PeriodLike): string {
  return period.label ?? formatPeriodShort(period);
}

/** The viewer's time zone (an IANA name, from the browser): period days are counted in it (ADR 0008). */
export function clientTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function formatDate(iso: string, lang: Lang): string {
  return new Intl.DateTimeFormat(lang === 'vi' ? 'vi-VN' : 'en-GB', {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso));
}

export const levelCode = (level: number | null): string => (level === null ? '–' : `L${level}`);

/** The user-facing message for any error, translated by code. */
export function errorMessage(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) return t(`errors.${error.code}`);
  return t('errors.INTERNAL');
}

/** API area rows → the map computeScores() expects. */
export function toStates(
  areas: readonly {
    areaId: string;
    selfLevel: number | null;
    isNa: boolean;
    confirmedLevel: number | null;
  }[],
): AreaStates {
  return Object.fromEntries(
    areas.map((a) => [
      a.areaId,
      {
        selfLevel: a.selfLevel as Level | null,
        isNa: a.isNa,
        confirmedLevel: a.confirmedLevel as Level | null,
      },
    ]),
  );
}
